import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, Clock, ShieldCheck, PiggyBank, Download,
  FileText, ChevronRight, Info, Calendar, Filter, Receipt, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PartnerShell } from "./Dashboard";
import { formatDateAU } from "@/lib/formatDateAU";

/**
 * Feb 2026 — Phase 1 Partner Ledger & Payouts page.
 *
 * Read-only. Fetches /api/partner/ledger, renders four headline
 * tiles (pending, approved, paid this month, lifetime paid), a
 * filter bar, a table of privacy-filtered rows, and a CSV
 * download link.
 *
 * All state controls the row list; the summary tiles ALWAYS reflect
 * the full ledger so partners see their true position regardless of
 * current filter selection.
 */

const STATUS_STYLE = {
  pending:          { pill: "bg-amber-100 text-amber-800",     dot: "bg-amber-500" },
  approved:         { pill: "bg-sky-100 text-sky-700",         dot: "bg-sky-500" },
  payout_scheduled: { pill: "bg-indigo-100 text-indigo-700",   dot: "bg-indigo-500" },
  paid:             { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  void:             { pill: "bg-slate-100 text-slate-500",     dot: "bg-slate-400" },
};

const STATUS_LABEL = {
  pending:          "pending",
  approved:         "approved",
  payout_scheduled: "payout scheduled",
  paid:             "paid",
  void:             "void",
};

const TYPE_LABEL = {
  commission:            "Partner commission",
  adjustment:            "Adjustment",
  postage_reimbursement: "Postage reimbursement",
};

const CURRENCY = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2,
});

// Status pill map shared with the Statements section.
const STATEMENT_STATUS_STYLE = {
  payout_scheduled: { pill: "bg-indigo-100 text-indigo-700",   dot: "bg-indigo-500", label: "Payout scheduled" },
  paid:             { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", label: "Paid" },
  cancelled:        { pill: "bg-slate-100 text-slate-500",     dot: "bg-slate-400",   label: "Cancelled" },
};

function StatementStatusPill({ status }) {
  const style = STATEMENT_STATUS_STYLE[status] || STATEMENT_STATUS_STYLE.cancelled;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

function StatusPill({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.void;
  const label = STATUS_LABEL[status] || status || "—";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

function SummaryTile({ label, amount, icon: Icon, tone, testid, hint }) {
  const TONES = {
    amber:   { bg: "bg-amber-50 border-amber-200",   icon: "text-amber-600" },
    sky:     { bg: "bg-sky-50 border-sky-200",       icon: "text-sky-600" },
    emerald: { bg: "bg-emerald-50 border-emerald-200", icon: "text-emerald-600" },
    slate:   { bg: "bg-white border-slate-200",       icon: "text-slate-600" },
  };
  const t = TONES[tone] || TONES.slate;
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 ${t.bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <Icon className={`w-4 h-4 ${t.icon}`} />
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900 font-mono">
        {CURRENCY.format(amount || 0)}
      </div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

export default function PartnerLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    ledger_type: "all",
    date_from: "",
    date_to: "",
  });
  // Feb 2026 · Change #3 — Statements tile support.
  // When set, we ALSO forward `payout_reference` to /partner/ledger
  // so the table below the tile snaps to just the rows in that run.
  // Cleared by the "Clear payout filter" chip.
  const [payoutRefFilter, setPayoutRefFilter] = useState("");
  const [statements, setStatements] = useState(null);
  const [statementsError, setStatementsError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.status !== "all")       params.status = filters.status;
      if (filters.ledger_type !== "all")  params.ledger_type = filters.ledger_type;
      if (filters.date_from)              params.date_from = filters.date_from;
      if (filters.date_to)                params.date_to = filters.date_to;
      if (payoutRefFilter)                params.payout_reference = payoutRefFilter;
      const { data } = await api.get("/partner/ledger", { params });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, payoutRefFilter]);

  useEffect(() => { load(); }, [load]);

  // Statements — independent fetch. A failure to load statements
  // must NOT hide the ledger below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/partner/statements");
        if (!cancelled) setStatements(data);
      } catch (e) {
        if (!cancelled) setStatementsError(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const downloadCSV = async () => {
    try {
      const resp = await api.get("/partner/ledger.csv", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partner-ledger-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadStatementPDF = async (ref) => {
    try {
      const resp = await api.get(
        `/partner/statements/${encodeURIComponent(ref)}/pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SCWT-Partner-Payout-Statement-${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement ${ref} downloaded.`);
    } catch (e) {
      // Axios blob errors need to be re-read to get the JSON detail
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || ""; } catch { /* ignore */ }
      }
      toast.error(detail || formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const totalRow = useMemo(
    () => rows.reduce((sum, r) => sum + (r.amount || 0), 0),
    [rows],
  );

  return (
    <PartnerShell active="ledger">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-600">Trade Partner</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" data-testid="partner-ledger-heading">Ledger &amp; Payouts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Read-only view of commissions, reimbursements and payout history for your account.
            All approvals, payments and adjustments are managed by Aware admin.
          </p>
        </header>

        {loading && !data ? (
          <div data-testid="partner-ledger-loading" className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            Loading your ledger…
          </div>
        ) : error ? (
          <div data-testid="partner-ledger-error" className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
            {error}
          </div>
        ) : (
          <>
            {/* ─── Summary tiles (always full-ledger, ignore filters) ─── */}
            <div data-testid="partner-ledger-summary" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <SummaryTile
                testid="partner-ledger-summary-pending"
                label="Pending" tone="amber" icon={Clock}
                amount={summary.pending_total}
              />
              <SummaryTile
                testid="partner-ledger-summary-approved"
                label="Approved / Payable" tone="sky" icon={ShieldCheck}
                amount={summary.approved_total}
                hint="Awaiting next monthly run"
              />
              <SummaryTile
                testid="partner-ledger-summary-paid-this-month"
                label="Paid this month" tone="emerald" icon={Wallet}
                amount={summary.paid_this_month}
              />
              <SummaryTile
                testid="partner-ledger-summary-lifetime"
                label="Lifetime paid" tone="slate" icon={PiggyBank}
                amount={summary.lifetime_paid}
              />
            </div>

            {/* ─── Payout notice ─── */}
            <div data-testid="partner-ledger-payout-note" className="mb-6 rounded-lg bg-cyan-50 border border-cyan-200 px-4 py-2.5 text-xs text-cyan-900 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-cyan-600" />
              <div>
                <span className="font-semibold">{summary.next_payout_note || "Partner payouts are reviewed monthly."}</span>
                <span className="ml-1 text-cyan-700">If a row is missing or looks wrong, contact Aware admin — we can&apos;t adjust it here.</span>
              </div>
            </div>

            {/* ─── Statements (Change #3, Feb 2026) ─── */}
            <section
              data-testid="partner-statements-section"
              className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-4"
            >
              <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-indigo-600" />
                  Payout statements
                  {statements?.rows && (
                    <span className="text-slate-400 font-normal">({statements.rows.length})</span>
                  )}
                </h2>
                <div className="text-[11px] text-slate-500">
                  Read-only view of every payout run your account has been included in.
                </div>
              </header>
              {statementsError ? (
                <div
                  data-testid="partner-statements-error"
                  className="p-4 text-xs text-rose-700 bg-rose-50 border-t border-rose-100"
                >
                  {statementsError}
                </div>
              ) : !statements ? (
                <div data-testid="partner-statements-loading" className="p-6 text-center text-xs text-slate-400">
                  Loading statements…
                </div>
              ) : statements.rows.length === 0 ? (
                <div
                  data-testid="partner-statements-empty"
                  className="p-6 text-center text-xs text-slate-500"
                >
                  No payout statements yet. Statements appear here whenever Aware admin groups your
                  approved commissions into a monthly payout run.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="partner-statements-table">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2 font-bold">Reference</th>
                        <th className="text-left px-4 py-2 font-bold">Status</th>
                        <th className="text-right px-4 py-2 font-bold">Rows</th>
                        <th className="text-right px-4 py-2 font-bold">Total</th>
                        <th className="text-left px-4 py-2 font-bold">Created</th>
                        <th className="text-left px-4 py-2 font-bold">Paid</th>
                        <th className="text-right px-4 py-2 font-bold">Statement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statements.rows.map((s) => {
                        const active = payoutRefFilter === s.payout_reference;
                        return (
                          <tr
                            key={s.payout_reference}
                            data-testid={`partner-statements-row-${s.payout_reference}`}
                            onClick={() => setPayoutRefFilter(active ? "" : s.payout_reference)}
                            className={`border-t border-slate-100 cursor-pointer transition-colors ${
                              active ? "bg-indigo-50/70" : "hover:bg-slate-50/60"
                            }`}
                          >
                            <td className="px-4 py-2 text-xs font-mono font-semibold text-indigo-700">
                              {s.payout_reference}
                            </td>
                            <td className="px-4 py-2"><StatementStatusPill status={s.status} /></td>
                            <td className="px-4 py-2 text-right text-xs font-semibold text-slate-800">{s.ledger_count}</td>
                            <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">
                              {CURRENCY.format(s.total_amount || 0)}
                            </td>
                            <td className="px-4 py-2 text-xs text-slate-600 font-mono">{fmtDateOnly(s.created_at)}</td>
                            <td className="px-4 py-2 text-xs text-slate-600 font-mono">{fmtDateOnly(s.paid_at)}</td>
                            <td className="px-4 py-2 text-right">
                              {s.status === "paid" ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadStatementPDF(s.payout_reference);
                                  }}
                                  data-testid={`partner-statement-pdf-btn-${s.payout_reference}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                                >
                                  <Download className="w-3 h-3" /> PDF
                                </button>
                              ) : (
                                <span
                                  className="text-[11px] text-slate-400"
                                  data-testid={`partner-statement-pdf-locked-${s.payout_reference}`}
                                  title="Available after this run is marked Paid"
                                >
                                  Available once paid
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <footer className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                Click a row to filter the ledger below to just the commissions in that payout run.
                Once a payout is marked <span className="font-semibold text-emerald-700">Paid</span>, you can download the statement PDF from the right-hand column.
              </footer>
            </section>

            {/* ─── Filter bar ─── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-900">Filters</h2>
                {payoutRefFilter && (
                  <span
                    data-testid="partner-ledger-payout-filter-chip"
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700"
                  >
                    Payout: <span className="font-mono">{payoutRefFilter}</span>
                    <button
                      type="button"
                      data-testid="partner-ledger-payout-filter-clear"
                      onClick={() => setPayoutRefFilter("")}
                      className="text-indigo-500 hover:text-indigo-800"
                      aria-label="Clear payout filter"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</label>
                  <select
                    data-testid="partner-ledger-filter-status"
                    value={filters.status}
                    onChange={(e) => set("status")(e.target.value)}
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none bg-white"
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved / Payable</option>
                    <option value="payout_scheduled">Payout scheduled</option>
                    <option value="paid">Paid</option>
                    <option value="void">Void</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</label>
                  <select
                    data-testid="partner-ledger-filter-type"
                    value={filters.ledger_type}
                    onChange={(e) => set("ledger_type")(e.target.value)}
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none bg-white"
                  >
                    <option value="all">All</option>
                    <option value="commission">Partner commission</option>
                    <option value="postage_reimbursement">Postage reimbursement</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">From</label>
                  <input
                    data-testid="partner-ledger-filter-date-from"
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => set("date_from")(e.target.value)}
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">To</label>
                  <input
                    data-testid="partner-ledger-filter-date-to"
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => set("date_to")(e.target.value)}
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    data-testid="partner-ledger-filter-clear-btn"
                    onClick={() => {
                      setFilters({ status:"all", ledger_type:"all", date_from:"", date_to:"" });
                      setPayoutRefFilter("");
                    }}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-testid="partner-ledger-csv-btn"
                    onClick={downloadCSV}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                  >
                    <Download className="w-3 h-3" /> CSV
                  </button>
                </div>
              </div>
            </div>

            {/* ─── Row table ─── */}
            <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-600" />
                  Ledger rows
                  <span className="text-slate-400 font-normal">({rows.length})</span>
                </h2>
                <div className="text-xs text-slate-500">
                  Row total: <span className="font-mono font-semibold text-slate-900">{CURRENCY.format(totalRow)}</span>
                </div>
              </header>
              {rows.length === 0 ? (
                <div data-testid="partner-ledger-empty" className="p-10 text-center text-slate-400 text-sm space-y-2">
                  <div>No ledger activity to show.</div>
                  <div className="text-[11px]">Commissions will appear here as your paid bookings roll through the pipeline.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="partner-ledger-table">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-bold">Date</th>
                        <th className="text-left px-4 py-2.5 font-bold">Booking</th>
                        <th className="text-left px-4 py-2.5 font-bold">Type</th>
                        <th className="text-left px-4 py-2.5 font-bold">Test / Package</th>
                        <th className="text-left px-4 py-2.5 font-bold">Suburb</th>
                        <th className="text-right px-4 py-2.5 font-bold">Amount</th>
                        <th className="text-left px-4 py-2.5 font-bold">Status</th>
                        <th className="text-left px-4 py-2.5 font-bold">Paid</th>
                        <th className="text-left px-4 py-2.5 font-bold">Payout ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.id}
                          data-testid={`partner-ledger-row-${r.booking_reference}`}
                          className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors"
                        >
                          <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{r.date || "—"}</td>
                          <td className="px-4 py-2.5 font-mono text-xs font-semibold">
                            {r.booking_reference ? (
                              <Link
                                to={`/partner/bookings/${r.booking_reference}`}
                                data-testid={`partner-ledger-row-booking-link-${r.booking_reference}`}
                                className="text-cyan-700 hover:text-cyan-900 hover:underline"
                              >
                                {r.booking_reference}
                              </Link>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-700">{TYPE_LABEL[r.ledger_type] || r.ledger_type}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-700 truncate max-w-[180px]" title={r.test_package || ""}>{r.test_package || "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-700">{r.suburb || "—"}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">
                            {CURRENCY.format(r.amount || 0)}
                          </td>
                          <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{r.paid_at || "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{r.payout_reference || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <footer className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-emerald-500" />
                <span>Amounts shown are those calculated by the ledger. Commission rates and formulas are managed by Aware admin.</span>
              </footer>
            </section>
          </>
        )}
      </div>
    </PartnerShell>
  );
}
